import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import api from '../../services/api';
import { X, Save } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { getCurrencySymbol } from '../../utils/currency';

interface PaymentFormProps {
    onClose: () => void;
    onSuccess: () => void;
    payment?: any;
    customers: any[];
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onClose, onSuccess, payment, customers }) => {
    const { currency } = useAuth();
    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: {
            customerId: '',
            projectId: '',
            amount: '',
            bankCharges: '',
            paymentDate: new Date().toISOString().split('T')[0],
            paymentMode: 'Cash',
            depositTo: 'Petty Cash',
            reference: '',
            notes: ''
        }
    });

    useEffect(() => {
        if (payment) {
            reset({
                ...payment,
                projectId: payment.projectId || '',
                paymentDate: new Date(payment.paymentDate).toISOString().split('T')[0],
            });
        }
    }, [payment, reset]);

    const onSubmit = async (data: any) => {
        try {
            if (payment) {
                await api.put(`/payments/${payment.id}`, data);
            } else {
                await api.post('/payments', data);
            }
            onSuccess();
        } catch (err) {
            console.error('Failed to save payment', err);
            alert('Failed to save payment');
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-xl font-semibold text-gray-800">
                        {payment ? 'Edit Payment' : 'Record Payment'}
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6">
                    <form id="payment-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">

                        {/* Customer */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name*</label>
                            <select
                                {...register('customerId', { required: 'Customer is required' })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                <option value="">Select Customer</option>
                                {customers.map((c: any) => (
                                    <option key={c.id} value={c.id}>{c.displayName}</option>
                                ))}
                            </select>
                            {errors.customerId && <p className="text-red-500 text-sm">{errors.customerId.message as string}</p>}
                        </div>

                        {/* Project (Optional) */}
                        {watch('customerId') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project (Optional)</label>
                                <select
                                    {...register('projectId')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="">General / No Project</option>
                                    {customers.find(c => c.id === watch('customerId'))?.projects?.map((p: any) => (
                                        <option key={p.id} value={p.id}>{p.projectName}</option>
                                    )) || customers.map(c => c.projects || []).flat().filter(p => p.customerId === watch('customerId')).map((p: any) => (
                                        <option key={p.id} value={p.id}>{p.projectName}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-6">
                            {/* Amount */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Amount Received ({currency})*</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    {...register('amount', { required: 'Amount is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                                {errors.amount && <p className="text-red-500 text-sm">{errors.amount.message as string}</p>}
                            </div>

                            {/* Bank Charges */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Charges (if any)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    {...register('bankCharges')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Payment Date */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date*</label>
                                <input
                                    type="date"
                                    {...register('paymentDate', { required: 'Date is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>

                            {/* Payment Mode */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode*</label>
                                <select
                                    {...register('paymentMode', { required: 'Payment Mode is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="Cash">Cash</option>
                                    <option value="Bank Transfer">Bank Transfer</option>
                                    <option value="Credit Card">Credit Card</option>
                                    <option value="Cheque">Cheque</option>
                                </select>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Deposit To */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Deposit To*</label>
                                <select
                                    {...register('depositTo', { required: 'Deposit To is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                    <option value="Petty Cash">Petty Cash</option>
                                    <option value="Undeposited Funds">Undeposited Funds</option>
                                </select>
                            </div>

                            {/* Reference */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Reference#</label>
                                <input
                                    type="text"
                                    {...register('reference')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                {...register('notes')}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>

                    </form>
                </div>

                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex gap-3">
                    <button
                        type="submit"
                        form="payment-form"
                        className="flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition"
                    >
                        <Save size={18} />
                        Save
                    </button>
                    <button
                        onClick={onClose}
                        className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-100 transition text-gray-700"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PaymentForm;
